import { useEffect, useRef, useState } from "react";
import { MdEmojiEmotions, MdLogout, MdGridView, MdKeyboardArrowDown, MdMoreHoriz, MdMic } from "react-icons/md";

export const LAYOUTS = {
  gallery: { label: "Gallery", count: 6, view: "grid" },
  spotlight: { label: "Speaker spotlight", count: 6, view: "spot" },
  sidebar: { label: "Speaker and sidebar", count: 6, view: "side" },
  large: { label: "Large meeting (12)", count: 12, view: "dense" },
  presentation: { label: "Presentation", count: 6, view: "pres" },
  light: { label: "Light gallery", count: 6, view: "grid", light: true },
};

const EMOJIS = ["👍", "👏", "😂", "🎉", "❤️"];

/* Shown on the phone bar. Everything else moves behind the ⋯ button. */
function isEssential(title = "") {
  return /^(un)?mute$/i.test(title) || /video$/i.test(title) || /^chat$/i.test(title) || /^leave$/i.test(title);
}

function isMoreOnly(title = "") {
  return /captions|poll|whiteboard/i.test(title);
}

function initials(name) {
  return (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

/* Columns and rows for n tiles, never more than maxCols per row. */
function gridShape(n, maxCols) {
  const cols = Math.min(maxCols, Math.max(1, Math.ceil(Math.sqrt(n))));
  const rows = Math.max(1, Math.ceil(n / cols));
  return { cols, rows };
}

function MicOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
      <path d="M3.3 2 2 3.3l5 5V12a3 3 0 0 0 4.6 2.5l1.5 1.5A4.9 4.9 0 0 1 7 12H5a7 7 0 0 0 6 6.9V21h2v-2.1a7 7 0 0 0 1.9-.5l4.8 4.8 1.3-1.3L3.3 2zM15 11.2V6a3 3 0 0 0-5.8-1.1L15 11.2z" />
    </svg>
  );
}

function VideoTile({ person, stream, small, fill, muted, onMute, isPinned, onClick }) {
  const videoRef = useRef(null);
  const live = !!stream && stream.getVideoTracks().length > 0;
  const showMic = person.id !== "screen";

  useEffect(() => {
    // Always muted: remote audio plays from the page's hidden elements, so this avoids echo.
    if (videoRef.current && videoRef.current.srcObject !== stream) {
      videoRef.current.srcObject = stream || null;
    }
  }, [stream]);

  const cls = ["wg-tile", small ? "wg-sm" : "", fill ? "wg-fill" : "", isPinned ? "wg-speaking" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={cls} onClick={onClick} role="button" tabIndex={0} aria-label={person.name}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="wg-video"
        style={{ display: live ? "block" : "none" }}
      />
      {!live && (
        <span
          className="wg-av"
          aria-hidden="true"
          style={{
            background: `linear-gradient(135deg, hsl(${person.hue} 80% 60%), hsl(${(person.hue + 40) % 360} 80% 45%))`,
          }}
        >
          {initials(person.name)}
        </span>
      )}
      {person.isHandRaised && <span className="wg-hand" aria-label="Hand raised">✋</span>}
      {person.reaction && <span className="wg-reaction" aria-hidden="true">{person.reaction}</span>}
      <span className="wg-tag">
        {showMic && (onMute ? (
          <button
            type="button"
            className="wg-mic-btn"
            title="Mute participant"
            aria-label="Mute participant"
            onClick={(e) => {
              e.stopPropagation();
              onMute();
            }}
          >
            <MdMic />
          </button>
        ) : (
          <span className={muted ? "wg-mic-off" : "wg-mic-on"} aria-label={muted ? "Muted" : "Mic on"}>
            {muted ? <MicOffIcon /> : <MdMic />}
          </span>
        ))}
        {person.name}
      </span>
    </div>
  );
}

/* One toolbar button: icon with its label underneath. */
function ToolButton({ title, icon, active, badge, onClick, danger, extra, more, moreOnly }) {
  const cls = [
    "wg-tb",
    active ? "wg-on" : "",
    danger ? "wg-tb-leave" : "",
    extra ? "wg-extra" : "",
    more ? "wg-more" : "",
    moreOnly ? "wg-moreonly" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={cls} title={title} aria-label={title} onClick={onClick}>
      <span className="wg-tb-ico">
        {icon}
        {badge ? <span className="wg-badge">{badge}</span> : null}
      </span>
      <span className="wg-tb-lbl">{title}</span>
    </button>
  );
}

/* Microphone or camera: the icon toggles, the chevron opens the source list. */
function ToolGroup({ main, menu, extra, moreOnly }) {
  return (
    <div className={`wg-tbg${main.active ? " wg-on" : ""}${extra ? " wg-extra" : ""}${moreOnly ? " wg-moreonly" : ""}`}>
      <div className="wg-tbg-row">
        <button type="button" className="wg-tb-main" title={main.title} aria-label={main.title} onClick={main.onClick}>
          {main.icon}
        </button>
        <button type="button" className="wg-tb-menu" title={menu.title} aria-label={menu.title} onClick={menu.onClick}>
          <MdKeyboardArrowDown />
        </button>
      </div>
      <span className="wg-tb-lbl">{main.title}</span>
    </div>
  );
}

export function WebinarGrid({
  participants = [],
  getStream = () => null,
  defaultLayout = "gallery",
  title = "Live class",
  selfMuted = false,
  screenStream = null,
  slide = null,
  showLayoutTabs = true,
  onToggleMic,
  onShare,
  isSharing = false,
  onRaiseHand,
  onReact,
  onLeave,
  controls = [],
  devicePanel = null,
  isHost = false,
  onMutePeer = null,
}) {
  const [layout, setLayout] = useState(defaultLayout);
  // When this user starts sharing a screen, show it full size in the Presentation layout.
  useEffect(() => { if (isSharing) setLayout("presentation"); }, [isSharing]);
  const [pinnedId, setPinnedId] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showViews, setShowViews] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const cfg = LAYOUTS[layout];
  const list = participants.slice(0, cfg.count);
  const pinned = list.find((p) => p.id === pinnedId) || list.find((p) => p.id !== "self") || list[0];
  const others = list.filter((p) => p !== pinned);
  const maxCols = typeof window !== "undefined" && window.innerWidth < 640 ? 2 : 5;
  const shape = gridShape(list.length, maxCols);

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  const tile = (p, small, fill = false) => (
    <VideoTile
      key={p.id}
      person={p}
      stream={getStream(p.id)}
      small={small}
      fill={fill}
      muted={p.id === "self" ? selfMuted : !!p.muted}
      onMute={isHost && p.id !== "self" && !p.muted && onMutePeer ? () => onMutePeer(p.id) : null}
      isPinned={pinned && p.id === pinned.id}
      onClick={() => setPinnedId(p.id)}
    />
  );

  function renderBody() {
    switch (cfg.view) {
      case "grid":
      case "dense":
        return (
          <div
            className="wg-grid"
            style={{
              gridTemplateColumns: `repeat(${shape.cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${shape.rows}, minmax(0, 1fr))`,
            }}
          >
            {list.map((p) => tile(p, cfg.view === "dense", true))}
          </div>
        );
      case "spot":
        return (
          <div className="wg-spot">
            <div className="wg-main">{pinned && tile(pinned, false)}</div>
            <div className="wg-strip">{others.map((p) => tile(p, true))}</div>
          </div>
        );
      case "side":
        return (
          <div className="wg-side">
            <div className="wg-main">{pinned && tile(pinned, false)}</div>
            <div className="wg-side-col">{others.map((p) => tile(p, true))}</div>
          </div>
        );
      case "pres": {
        const main = screenStream ? (
          <VideoTile person={{ id: "screen", name: "Screen share", hue: 200 }} stream={screenStream} fill />
        ) : slide ? (
          <div className="wg-slide">
            <p className="wg-kicker">{slide.kicker}</p>
            <h3>{slide.title}</h3>
            <ul>{slide.points.map((pt) => <li key={pt}>{pt}</li>)}</ul>
          </div>
        ) : null;
        return (
          <div className="wg-pres">
            <div className="wg-main">{main}</div>
            <div className="wg-side-col">{list.map((p) => tile(p, true))}</div>
          </div>
        );
      }
      default:
        return null;
    }
  }

  const groups = [];
  for (const c of controls) {
    const last = groups[groups.length - 1];
    if (/source/i.test(c.title || "") && last && !last.menu) {
      last.menu = c;
    } else {
      groups.push({ main: c, menu: null });
    }
  }

  return (
    <div className="wg-root">
      <style>{WG_CSS}</style>

      <div className={`wg-stage${cfg.light ? " wg-stage-plain" : ""}`}>
        <div className={`wg-meet${cfg.light ? " wg-meet-light" : ""}`}>
          <div className="wg-top">
            <div className="wg-top-left">
              <span className="wg-rec">
                <i className="wg-rec-dot" aria-hidden="true" />
                {title}
              </span>
              <span aria-label={`Elapsed ${mm} minutes ${ss} seconds`}>{mm}:{ss}</span>
            </div>

            <div className={`wg-bar${moreOpen ? " wg-open" : ""}`}>
              {showLayoutTabs && (
                <div className="wg-viewpick wg-extra">
                  <button
                    type="button"
                    className="wg-tb"
                    aria-haspopup="menu"
                    aria-expanded={showViews}
                    title="Change View"
                    onClick={() => setShowViews((v) => !v)}
                  >
                    <span className="wg-tb-ico"><MdGridView /></span>
                    <span className="wg-tb-lbl">Change View</span>
                  </button>
                  {showViews && (
                    <div className="wg-viewmenu" role="menu">
                      {Object.keys(LAYOUTS).map((key) => (
                        <button
                          key={key}
                          type="button"
                          role="menuitemradio"
                          aria-checked={layout === key}
                          className={`wg-viewitem${layout === key ? " wg-viewitem-on" : ""}`}
                          onClick={() => {
                            setLayout(key);
                            setPinnedId(null);
                            setShowViews(false);
                          }}
                        >
                          {LAYOUTS[key].label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {groups.map((g, i) =>
                g.menu ? (
                  <ToolGroup key={`g-${i}`} main={g.main} menu={g.menu} extra={!isEssential(g.main.title)} moreOnly={isMoreOnly(g.main.title)} />
                ) : (
                  <ToolButton
                    key={`c-${i}`}
                    title={g.main.title}
                    icon={g.main.icon}
                    active={g.main.active}
                    badge={g.main.badge}
                    onClick={g.main.onClick}
                    extra={!isEssential(g.main.title)}
                    moreOnly={isMoreOnly(g.main.title)}
                  />
                )
              )}

              <ToolButton title="React" icon={<MdEmojiEmotions />} onClick={() => setShowEmoji((v) => !v)} extra />
              <ToolButton
                title={moreOpen ? "Less" : "More"}
                icon={<MdMoreHoriz />}
                onClick={() => setMoreOpen((v) => !v)}
                more
              />
              <ToolButton title="Leave" icon={<MdLogout />} onClick={() => onLeave?.()} danger />
            </div>
          </div>

          {showEmoji && (
            <div className="wg-emoji-row">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="wg-emoji"
                  onClick={() => {
                    onReact?.(e);
                    setShowEmoji(false);
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}

          {devicePanel}

          <div className="wg-body">{renderBody()}</div>
        </div>
      </div>
    </div>
  );
}

const WG_CSS = `
.wg-root { font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #fff; min-height: 100vh; display: flex; flex-direction: column; background: #000; }
.wg-root *, .wg-root *::before, .wg-root *::after { box-sizing: border-box; }
.wg-stage { flex: 1; display: flex; min-height: 0; padding: 8px; }
.wg-stage-plain { background: #dfe8f7; }
.wg-meet { flex: 1; display: flex; flex-direction: column; min-height: 0; background: #0f1523; border-radius: 12px; padding: 10px 12px; color: #fff; }
.wg-meet-light { background: #fff; color: #0b1020; }
.wg-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; padding: 2px 4px 8px; font-size: 14px; color: rgba(255,255,255,.8); }
.wg-meet-light .wg-top { color: #475569; }
.wg-top-left { display: flex; align-items: center; gap: 14px; padding-top: 6px; }
.wg-rec { display: inline-flex; align-items: center; gap: 8px; }
.wg-rec-dot { width: 9px; height: 9px; border-radius: 50%; background: #ff5a4e; animation: wg-blink 1.4s ease-in-out infinite; }
@keyframes wg-blink { 50% { opacity: .35; } }
.wg-bar { display: flex; align-items: flex-start; justify-content: flex-end; flex-wrap: wrap; gap: 2px; margin-left: auto; }
.wg-tb { position: relative; display: inline-flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; min-width: 60px; padding: 4px 6px; border: 0; border-radius: 8px; background: transparent; color: #fff; cursor: pointer; font: 500 11px system-ui; }
.wg-tb:hover { background: rgba(255,255,255,.08); }
.wg-tb-ico { display: grid; place-items: center; width: 34px; height: 28px; position: relative; border-radius: 8px; }
.wg-tb-ico svg { width: 20px; height: 20px; }
.wg-tb-lbl { white-space: nowrap; font: 500 11px system-ui; }
.wg-tb.wg-on .wg-tb-ico { background: #0d7377; }
.wg-tb-leave .wg-tb-ico { background: #ff5a4e; border-radius: 999px; }
.wg-tb-leave:hover { background: rgba(255,90,78,.15); }
.wg-tbg { display: inline-flex; flex-direction: column; align-items: center; gap: 3px; min-width: 60px; padding: 4px 2px; border-radius: 8px; }
.wg-tbg:hover { background: rgba(255,255,255,.08); }
.wg-tbg-row { display: inline-flex; align-items: center; }
.wg-tb-main, .wg-tb-menu { border: 0; background: transparent; color: #fff; cursor: pointer; display: grid; place-items: center; height: 28px; padding: 0; }
.wg-tb-main { width: 34px; border-radius: 8px 0 0 8px; }
.wg-tb-menu { width: 18px; border-radius: 0 8px 8px 0; }
.wg-tb-main svg, .wg-tb-menu svg { width: 20px; height: 20px; }
.wg-tbg.wg-on .wg-tb-main, .wg-tbg.wg-on .wg-tb-menu { background: #0d7377; }
.wg-badge { position: absolute; top: -4px; right: -6px; min-width: 18px; height: 18px; border-radius: 999px; background: #ff5a4e; color: #fff; font: 700 11px system-ui; display: grid; place-items: center; padding: 0 4px; }
.wg-more { display: inline-flex; }
.wg-moreonly { display: none; }
.wg-bar.wg-open .wg-moreonly { display: flex; }
.wg-viewpick { position: relative; }
.wg-viewpick .wg-tb-lbl { white-space: normal; text-align: center; max-width: 96px; line-height: 1.2; }
.wg-viewmenu { position: absolute; top: calc(100% + 6px); right: 0; z-index: 300; min-width: 240px; background: #0f1523; border: 1px solid rgba(255,255,255,.12); border-radius: 12px; padding: 6px; box-shadow: 0 16px 40px rgba(0,0,0,.35); display: flex; flex-direction: column; }
.wg-viewitem { border: 0; background: transparent; color: #fff; text-align: left; padding: 10px 12px; border-radius: 8px; font: 600 14px system-ui; cursor: pointer; }
.wg-viewitem:hover { background: #1b2233; }
.wg-viewitem-on { background: #1557c8; }
.wg-body { flex: 1; min-height: 0; display: flex; flex-direction: column; margin-top: 6px; }
.wg-grid { flex: 1; min-height: 0; display: grid; gap: 8px; }
.wg-spot { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 8px; }
.wg-spot .wg-main { flex: 1; min-height: 0; display: flex; }
.wg-spot .wg-main > .wg-tile { height: 100%; aspect-ratio: auto; }
.wg-strip { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; }
.wg-side { flex: 1; min-height: 0; display: grid; grid-template-columns: 1fr 210px; gap: 8px; }
.wg-side .wg-main { min-height: 0; display: flex; }
.wg-side .wg-main > .wg-tile { height: 100%; aspect-ratio: auto; }
.wg-side-col { display: flex; flex-direction: column; gap: 8px; min-height: 0; overflow-y: auto; }
.wg-pres { flex: 1; min-height: 0; display: grid; grid-template-columns: 1fr 170px; gap: 8px; }
.wg-pres .wg-main { min-height: 0; display: flex; }
.wg-pres .wg-main > .wg-tile { height: 100%; aspect-ratio: auto; }
.wg-slide { border-radius: 12px; padding: 36px; color: #fff; background: linear-gradient(135deg, #1557c8, #09b5e6); width: 100%; }
.wg-kicker { margin: 0 0 10px; font-size: 13px; opacity: .85; }
.wg-slide h3 { margin: 0 0 14px; font-size: 30px; line-height: 1.1; color: #fff; }
.wg-slide ul { margin: 0; padding-left: 20px; line-height: 1.7; font-size: 16px; }
.wg-tile { position: relative; aspect-ratio: 16 / 9; width: 100%; border-radius: 12px; cursor: pointer; background: #1b2233; display: flex; align-items: center; justify-content: center; overflow: hidden; transition: box-shadow .4s ease; }
.wg-tile.wg-fill { aspect-ratio: auto; height: 100%; min-height: 0; }
.wg-tile.wg-sm { border-radius: 10px; }
.wg-tile.wg-speaking { box-shadow: 0 0 0 3px #22d3ee, 0 0 28px rgba(34,211,238,.35); }
.wg-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.wg-av { width: 68px; height: 68px; border-radius: 999px; display: grid; place-items: center; font: 700 20px system-ui; color: #fff; }
.wg-sm .wg-av { width: 40px; height: 40px; font-size: 13px; }
.wg-tag { position: absolute; left: 8px; bottom: 8px; display: inline-flex; align-items: center; gap: 6px; background: rgba(11,16,32,.72); color: #fff; padding: 4px 8px; border-radius: 6px; font-size: 12px; white-space: nowrap; max-width: calc(100% - 16px); overflow: hidden; text-overflow: ellipsis; }
.wg-sm .wg-tag { font-size: 11px; padding: 3px 6px; }
.wg-tag svg { width: 12px; height: 12px; flex: none; }
.wg-hand { position: absolute; top: 8px; right: 8px; font-size: 26px; }
.wg-reaction { position: absolute; inset: 0; display: grid; place-items: center; font-size: 56px; pointer-events: none; background: rgba(0,0,0,.2); }
.wg-meet-light .wg-tile { background: #e8eefb; color: #0b1020; }
.wg-meet-light .wg-tile.wg-speaking { box-shadow: 0 0 0 3px #1557c8, 0 0 22px rgba(21,87,200,.25); }
.wg-meet-light .wg-tag { background: rgba(255,255,255,.9); color: #0b1020; }
.wg-meet-light .wg-tb, .wg-meet-light .wg-tbg, .wg-meet-light .wg-tb-main, .wg-meet-light .wg-tb-menu { color: #0b1020; }
.wg-emoji-row { display: flex; gap: 8px; justify-content: center; margin: 8px 0; }
.wg-emoji { border: 0; background: #1b2233; border-radius: 999px; padding: 6px 12px; font-size: 22px; cursor: pointer; }
@media (max-width: 640px) {
  .wg-stage { padding: 0; }
  .wg-meet { padding: 8px 8px 96px; border-radius: 0; min-height: 100vh; }
  .wg-top { flex-direction: column; align-items: stretch; padding: 0 0 6px; }
  .wg-top-left { padding-top: 0; justify-content: space-between; }
  .wg-bar {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 200;
    margin: 0; justify-content: space-around; flex-wrap: nowrap;
    background: #0f1523; border-top: 1px solid rgba(255,255,255,.1);
    padding: 6px 4px calc(6px + env(safe-area-inset-bottom));
  }
  .wg-bar.wg-open { flex-wrap: wrap; max-height: 60vh; overflow-y: auto; }
  .wg-tb, .wg-tbg { min-width: 54px; }
  .wg-extra { display: none; }
  .wg-bar.wg-open .wg-extra { display: flex; }
  .wg-more { display: inline-flex; }
  .wg-bar .wg-viewmenu { top: auto; bottom: calc(100% + 6px); right: auto; left: 0; }
  .wg-body { flex: none; }
  .wg-grid { flex: none; grid-template-rows: none !important; }
  .wg-tile.wg-fill { height: auto; aspect-ratio: 3 / 4; }
  .wg-side, .wg-pres { grid-template-columns: 1fr; }
  .wg-side-col { flex-direction: row; overflow-x: auto; }
  .wg-side-col .wg-tile { width: 140px; flex: none; }
  .wg-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .wg-slide { padding: 20px; }
  .wg-slide h3 { font-size: 22px; }
}
.wg-mic-on, .wg-mic-off { display: inline-grid; place-items: center; flex: none; }
.wg-mic-on { color: #34d399; }
.wg-mic-off { color: #f87171; }
.wg-mic-btn { border: 0; padding: 0; width: 20px; height: 20px; border-radius: 999px; display: inline-grid; place-items: center; cursor: pointer; flex: none; background: rgba(52,211,153,.2); color: #34d399; }
.wg-mic-btn:hover { background: #ef4444; color: #fff; }
@media (prefers-reduced-motion: reduce) {
  .wg-rec-dot { animation: none; }
  .wg-tile { transition: none; }
}
`;
